import Button from '@/common/components/Button'
import MaskedTextField from '@/common/components/MaskedTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { numberMask } from '@/common/constants/masks.constant'
import { useCalendarTimezones } from '@/common/hooks/queries/useCalendarTimezones'
import { usePrinterMachines } from '@/common/hooks/queries/usePrinterMachines'
import useDebounce from '@/common/hooks/useDebounce'
import { CalendarTimezoneTransformer } from '@/common/services/calendarTimezoneTransformer'
import { Mapper } from '@/common/services/mapper'
import { PrinterMachineData } from '@/common/types/printerMachine.type'
import { FormTransformers } from '@/common/utils/formTransformers'
import { CompanyData } from '@/features/companies/types/companies.type'
import { equipmentDriverLicenseRequirementsForConsumption, equipmentDriverLicenseRequirementsForConsumptionOptions } from '@/features/privateCompanies/constants/politics/generalPolitics/equipmentDriverLicenseRequirementsForConsumption.constant'
import { logReasonRequirement, logReasonRequirementOptions } from '@/features/privateCompanies/constants/politics/generalPolitics/logReasonRequirement.constant'
import { trackIntegrationMode, trackIntegrationModeOptions } from '@/features/privateCompanies/constants/politics/generalPolitics/trackIntegrationMode.constant'
import { companyPriceSurveyMode, companyPriceSurveyModeOptions } from '@/features/privateCompanies/constants/priceSurvey/companyPriceSurveyMode.constant'
import { useUpdateGeneralPolitics } from '@/features/privateCompanies/hooks/politics/useUpdateGeneralPolitics'
import {
  UpdateGeneralPoliticsSchema,
  updateGeneralPoliticsSchema,
} from '@/features/privateCompanies/schemas/politics/updateGeneralPolitics.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { requireBillReviewOptions } from '../../Politics/Show/constants/requireBillReview.constant'

interface UpdateGeneralPoliticsModalProps {
  company?: CompanyData
}

export const UpdateGeneralPoliticsModal = ({ company }: UpdateGeneralPoliticsModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchPrinterMachine, setSearchPrinterMachine] = useDebounce('')
  const [searchTimezone, setSearchTimezone] = useDebounce('')

  const { data: printerMachines } = usePrinterMachines({
    filters: {
      search: searchPrinterMachine,
    },
    mapper: Mapper.mapToOptions<PrinterMachineData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: timezones } = useCalendarTimezones({
    filters: { search: searchTimezone },
    mapper: Mapper.mapWithTransformer({ transformer: CalendarTimezoneTransformer.optionsTransformer() }),
  })

  const {
    control,
    handleSubmit,
    setError,
    reset,
    register,
    formState: { errors },
  } = useForm<UpdateGeneralPoliticsSchema>({
    resolver: zodResolver(updateGeneralPoliticsSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['company', company?.id] })
    setIsOpen(false)
  }

  const { mutate: updateGeneralPolitics, isPending: updateGeneralPoliticsIsPending } = useUpdateGeneralPolitics({
    companyId: company?.id,
    onSuccess,
    onError: FormTransformers.errorsTransformer<UpdateGeneralPoliticsSchema>({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<UpdateGeneralPoliticsSchema> = (data) => {
    updateGeneralPolitics(data)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        company: {
          printer_machine_id: company?.printer_machine
            ? { value: company?.printer_machine.id, label: company?.printer_machine.display_name }
            : undefined,
          time_zone_id: company?.time_zone
            ? { value: company?.time_zone.id, label: company?.time_zone.display_name }
            : undefined,
          price_survey_mode: company?.price_survey_mode
            ? { value: company?.price_survey_mode, label: companyPriceSurveyMode[company?.price_survey_mode] }
            : undefined,
          tracker_integration_mode: company?.tracker_integration_mode
            ? { value: company?.tracker_integration_mode, label: trackIntegrationMode[company?.tracker_integration_mode] }
            : undefined,
          update_equipment_log_reason_requirement: company?.update_equipment_log_reason_requirement
            ? { value: company?.update_equipment_log_reason_requirement, label: logReasonRequirement[company?.update_equipment_log_reason_requirement] }
            : undefined,
          update_user_log_reason_requirement: company?.update_user_log_reason_requirement
            ? { value: company?.update_user_log_reason_requirement, label: logReasonRequirement[company?.update_user_log_reason_requirement] }
            : undefined,
          equipment_driver_license_requirements_for_consumptions: company?.equipment_driver_license_requirements_for_consumptions
            ? { value: company?.equipment_driver_license_requirements_for_consumptions, label: equipmentDriverLicenseRequirementsForConsumption[company?.equipment_driver_license_requirements_for_consumptions] }
            : undefined,
          require_bill_review: company?.require_bill_review 
            ? requireBillReviewOptions.find(({ value }) => value === company?.require_bill_review,) 
            : undefined,
        },
        cards_quota: company?.cards_quota ? String(company?.cards_quota) : undefined
      })
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_general_politics_modal"
          operationKlassToCheckPermission="Admin::Client::Companies::UpdatePoliticsGeneralInformation"
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      bodyClassName='max-h-[calc(90vh-72px-48px)] overflow-auto'
      title="Editando políticas gerais"
      dataTestid="submit_update_general_politics"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateGeneralPoliticsIsPending}
      confirmButtonIsLoading={updateGeneralPoliticsIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="company.printer_machine_id"
              label="Impressora responsável pelos cartões da empresa"
              errorMessage={errors?.company?.printer_machine_id?.message}
              options={printerMachines}
              onInputChange={setSearchPrinterMachine}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.time_zone_id"
              label="Timezone"
              errorMessage={errors?.company?.time_zone_id?.message}
              options={timezones}
              onInputChange={setSearchTimezone}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.price_survey_mode"
              label="Pesquisa de preço"
              errorMessage={errors?.company?.price_survey_mode?.message}
              options={companyPriceSurveyModeOptions}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.tracker_integration_mode"
              label="Modo de integração do rastreador"
              errorMessage={errors?.company?.tracker_integration_mode?.message}
              options={trackIntegrationModeOptions}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.update_equipment_log_reason_requirement"
              label="Solicitar motivo na edição de equipamento"
              errorMessage={errors?.company?.update_equipment_log_reason_requirement?.message}
              options={logReasonRequirementOptions}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.update_user_log_reason_requirement"
              label="Solicitar motivo na edição de usuário"
              errorMessage={errors?.company?.update_user_log_reason_requirement?.message}
              options={logReasonRequirementOptions}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <MaskedTextField
              {...register("cards_quota")}
              label='Quantidade máxima de cartões'
              mask={numberMask(1000000000, false)}
              errorMessage={errors.cards_quota?.message}
              disabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.equipment_driver_license_requirements_for_consumptions"
              label="Validar compatibilidade da CNH"
              errorMessage={errors?.company?.equipment_driver_license_requirements_for_consumptions?.message}
              options={equipmentDriverLicenseRequirementsForConsumptionOptions}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.require_bill_review"
              label="Permitir rejeição de NF pelo cliente"
              errorMessage={errors?.company?.require_bill_review?.message}
              options={requireBillReviewOptions}
              isDisabled={updateGeneralPoliticsIsPending}
              markAsRequired
            />
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
